import React from 'react';
import { organisationsAPI } from '../../../organisations/api/organisationsAPI';
import { useStudentSection } from '../../hooks/useStudentSection';
import EmptyState from '../common/EmptyState';
import { EM_DASH, longDate, orDash } from '../../lib/format';

const fetchSchoolRecord = (studentId) => organisationsAPI.getStudentSchoolRecord(studentId);

/** "68%", "91.5%", "0%": the school's own figure, without a trailing ".0". */
const percent = (value) => (value == null ? EM_DASH : `${Number(value.toFixed(1))}%`);
const lates = (n) => (n == null ? `${EM_DASH} lates` : `${n} ${n === 1 ? 'late' : 'lates'}`);

/** "68% · B", "74%", "B", or a dash when the subject has no result at this point. */
const resultText = (result) => {
  const parts = [];
  if (result?.percentage != null) parts.push(percent(result.percentage));
  if (result?.grade) parts.push(result.grade);
  return parts.length ? parts.join(' · ') : EM_DASH;
};

const NO_DATA = 'No school data for this student yet.';

/**
 * The school's academic record for a student, from
 * GET /api/organisations/students/<id>/school-record: predicted grades with
 * UCAS points, attendance, and a row per subject with a column per
 * assessment point in the school's order, then the school's prediction.
 */
const StudentSchoolRecordPanel = ({ studentId }) => {
  const { status, data, message } = useStudentSection(
    fetchSchoolRecord, studentId, 'Could not load the school record. Please try again.',
  );

  if (status === 'loading') return <p className="td-loading">Loading school record…</p>;
  if (status === 'forbidden') return <EmptyState tone="warning" title="You don't have access to this." />;
  if (status === 'off') return <EmptyState title={NO_DATA} />;
  if (status === 'error') return <EmptyState tone="warning" title="Could not load the school record" body={message} />;
  if (!data.subjects.length) return <EmptyState title={NO_DATA} />;

  return (
    <div className="td-schooldata">
      {data.data_as_of ? <p className="td-schooldata__asof">{`School data as of ${longDate(data.data_as_of)}`}</p> : null}

      <div className="td-schooldata__summary">
        <p>Predicted <strong>{orDash(data.predicted_grades)}</strong>{` · ${orDash(data.ucas_points)} UCAS points`}</p>
        <p>{`Attendance ${percent(data.attendance_percentage)} · ${lates(data.lates)}`}</p>
      </div>

      <div className="td-tablewrap">
        <table className="td-table">
          <thead>
            <tr>
              <th scope="col">Subject</th>
              {data.assessment_points.map((code) => (
                <th key={code} scope="col" className="td-table__num">{code}</th>
              ))}
              <th scope="col" className="td-table__num">School prediction</th>
            </tr>
          </thead>
          <tbody>
            {data.subjects.map((s) => (
              <tr key={s.subject}>
                <td><span className="td-table__name">{s.subject}</span></td>
                {data.assessment_points.map((code) => (
                  <td key={code} className="td-table__num">{resultText(s.results[code])}</td>
                ))}
                <td className="td-table__num">{orDash(s.predicted_grade)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default StudentSchoolRecordPanel;
